import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approve_vendor,
  fetch_vendor_detail,
  fetch_vendor_list,
  reject_vendor,
  suspend_vendor,
  set_vendor_payout,
  request_vendor_payout,
  reveal_vendor_payout,
  verify_vendor_document,
  set_service_visibility,
  update_scheduling,
  type PayoutInput,
} from './api';
import { api } from '@/lib/api';
import { build_params } from '@/lib/list-query';
import type { SortState } from '@/hooks/use-url-state';
import type {
  ListFilters,
  ServiceVisibilityInput,
  UpdateSchedulingRequest,
  VendorListResponse,
} from './types';

const KEYS = {
  list: (filters: ListFilters, sort: SortState) => ['admin-vendor-list', filters, sort] as const,
  detail: (id: string) => ['admin-vendor-detail', id] as const,
};

export function use_vendor_list(filters: ListFilters, sort: SortState = null) {
  return useQuery({
    queryKey: KEYS.list(filters, sort),
    queryFn: () => fetch_vendor_list(filters, sort),
    // Keep the current page on screen while the next one loads, so paging and
    // sorting never blank the table.
    placeholderData: (prev) => prev,
  });
}

/**
 * Counts for the metric strip, scoped to whatever segment is in view.
 *
 * Each tile asks the list endpoint for a single row and reads `total` off the
 * envelope — cheap (a COUNT plus a one-row page), always consistent with the
 * list it links to, and it needs no bespoke stats endpoint per module.
 */
export function use_vendor_counts(base: Pick<ListFilters, 'statuses'>) {
  const count_of = async (extra: Partial<ListFilters>): Promise<number> => {
    const res = await api.get<VendorListResponse>('/admin/vendors', {
      params: build_params({ ...base, ...extra, page_size: 1 } as Record<
        string,
        string | number | undefined
      >),
    });
    return res.data.total;
  };

  const month_start = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  })();

  return useQuery({
    queryKey: ['admin-vendor-counts', base.statuses ?? 'all'],
    queryFn: async () => {
      const [total, awaiting_review, live, unpayable, blocked, joined_this_month] =
        await Promise.all([
          count_of({}),
          // Deliberately NOT scoped to the segment: the review queue is the
          // module's standing job and its size should read the same from
          // wherever you are looking.
          count_of({ statuses: 'DOCUMENTS_SUBMITTED' }),
          count_of({ statuses: 'VERIFIED', is_active: 'true' }),
          // Verified but unsettleable — takes bookings, cannot be paid.
          count_of({ statuses: 'VERIFIED', has_payout: 'false' }),
          count_of({ statuses: 'REJECTED,SUSPENDED' }),
          count_of({ from: month_start }),
        ]);
      return { total, awaiting_review, live, unpayable, blocked, joined_this_month };
    },
    staleTime: 60_000,
  });
}

export function use_vendor_detail(id: string | undefined) {
  return useQuery({
    queryKey: id ? KEYS.detail(id) : ['admin-vendor-detail', 'noop'],
    queryFn: () => fetch_vendor_detail(id as string),
    enabled: typeof id === 'string' && id.length > 0,
  });
}

function use_invalidate_vendor(id: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: KEYS.detail(id) });
    qc.invalidateQueries({ queryKey: ['admin-vendor-list'] });
  };
}

export function use_approve_vendor(id: string) {
  const invalidate = use_invalidate_vendor(id);
  return useMutation({
    mutationFn: () => approve_vendor(id),
    onSuccess: invalidate,
  });
}

export function use_reject_vendor(id: string) {
  const invalidate = use_invalidate_vendor(id);
  return useMutation({
    mutationFn: (reason: string) => reject_vendor(id, reason),
    onSuccess: invalidate,
  });
}

export function use_suspend_vendor(id: string) {
  const invalidate = use_invalidate_vendor(id);
  return useMutation({
    mutationFn: (reason: string) => suspend_vendor(id, reason),
    onSuccess: invalidate,
  });
}

/**
 * The same three status transitions, but with the id supplied per call.
 *
 * The hooks above close over one vendor, which is right on a detail page and
 * useless in a list where the row decides the target. Approve takes no reason
 * (the decision is the document review itself); reject and suspend both
 * require one, because a vendor who is told "no" has to be told why.
 */
export function use_vendor_status_actions() {
  const qc = useQueryClient();
  const invalidate = (id: string) => {
    void qc.invalidateQueries({ queryKey: KEYS.detail(id) });
    void qc.invalidateQueries({ queryKey: ['admin-vendor-list'] });
    void qc.invalidateQueries({ queryKey: ['admin-vendor-counts'] });
    // The sidebar badge and the dashboard queue both count pending KYC.
    void qc.invalidateQueries({ queryKey: ['admin-stats'] });
  };

  const approve = useMutation({
    mutationFn: (id: string) => approve_vendor(id),
    onSuccess: (_d, id) => invalidate(id),
  });
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => reject_vendor(id, reason),
    onSuccess: (_d, { id }) => invalidate(id),
  });
  const suspend = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => suspend_vendor(id, reason),
    onSuccess: (_d, { id }) => invalidate(id),
  });

  return { approve, reject, suspend };
}

/**
 * Fill in payout details for a legacy vendor that has none.
 *
 * Invalidates the vendor list and counts too, because "cannot be paid" is a
 * metric tile and a filter, not just a field on this page.
 */
export function use_set_vendor_payout(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PayoutInput) => set_vendor_payout(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.detail(id) });
      void qc.invalidateQueries({ queryKey: ['admin-vendor-list'] });
      void qc.invalidateQueries({ queryKey: ['admin-vendor-counts'] });
    },
  });
}

/**
 * Ask the vendor to add their own payout details.
 *
 * Invalidates the detail query so the panel flips to "asked <when>, by <who>"
 * without a reload — that state is the whole point of recording the request.
 */
export function use_request_vendor_payout(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => request_vendor_payout(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.detail(id) });
    },
  });
}

/**
 * Tick or untick one KYC document.
 *
 * Invalidates the vendor detail rather than patching the row: the Documents tab
 * count and the badge both read from it, and two sources of truth for "is this
 * verified" is how the flag ended up meaningless in the first place.
 */
export function use_verify_vendor_document(vendor_id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ doc_id, verified }: { doc_id: string; verified: boolean }) =>
      verify_vendor_document(vendor_id, doc_id, verified),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.detail(vendor_id) });
    },
  });
}

/**
 * Fetch the vendor's real payout destination for the clipboard.
 *
 * A mutation rather than a query on purpose: react-query would cache a query, and a
 * cached bank account is exactly what this must not create. Nothing here keeps the
 * result — the caller copies it and drops it.
 */
export function use_reveal_vendor_payout(id: string) {
  return useMutation({ mutationFn: () => reveal_vendor_payout(id) });
}

/**
 * Hide or restore one service.
 *
 * Invalidates the whole vendor detail rather than patching the row: the services
 * badge, the metric tile and the catalog all count live services, and three
 * places disagreeing about one toggle is worse than a refetch.
 */
export function use_set_service_visibility(vendor_id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ service_id, ...input }: ServiceVisibilityInput & { service_id: string }) =>
      set_service_visibility(vendor_id, service_id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.detail(vendor_id) });
    },
  });
}

export function use_update_scheduling(id: string) {
  const invalidate = use_invalidate_vendor(id);
  return useMutation({
    mutationFn: (input: UpdateSchedulingRequest) => update_scheduling(id, input),
    onSuccess: invalidate,
  });
}
